import { Home, BarChart2, Clock, Fuel, Gauge, Users, CreditCard, Settings, Droplet, LogOut } from 'lucide-react'
import { PRODUCTS, RAIL, productLines, sellingMeta } from '../calibration/constants'

// Matches NavBar's wording so the same account reads the same on either shell.
const ROLE_LABELS = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
}

// Desktop-only navigation for admins (docs/design/station-calibration-console.html).
// Below the breakpoint AppShell hides this and the bottom navbar takes over.
export const SIDE_NAV_ITEMS = [
  { id: 'home', label: 'Home', Icon: Home },
  { id: 'analytics', label: 'Analytics', Icon: BarChart2 },
  { id: 'history', label: 'History', Icon: Clock },
  { id: 'credit', label: 'Credit Sales', Icon: Fuel },
  { id: 'calibration', label: 'Station Calibration', Icon: Gauge },
  { id: 'team', label: 'Users & Team', Icon: Users },
  { id: 'billing', label: 'Billing & Plan', Icon: CreditCard },
  { id: 'settings', label: 'Settings', Icon: Settings },
]

/**
 * The org → stations → products tree that unfolds under Station Calibration.
 * It is how you reach any station in one click; the console's canvas follows
 * the same `calNav` state, so the rail and the page never disagree.
 */
function CalibrationTree({ organizations, stations, calNav, onCalNavChange, onTabChange }) {
  const go = (next) => { onCalNavChange(next); onTabChange('calibration') }
  const activeStation = stations.find((s) => s.id === calNav.stationId)
  const orgName = organizations.find((o) => o.id === (activeStation?.organizationId ?? organizations[0]?.id))?.name

  const rowBase = 'flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-left transition-colors'
  const label = 'px-2 pb-1.5 pt-2.5 text-[10.5px] font-semibold uppercase tracking-[.09em]'

  return (
    <div className="ml-[19px] border-l pl-3.5" style={{ borderColor: 'rgba(255,255,255,.09)' }}>
      <button
        onClick={() => go({ view: 'org', stationId: null })}
        className={`${rowBase} text-[12.5px] font-semibold`}
        style={{
          color: !calNav.stationId ? RAIL.bright : RAIL.text,
          background: !calNav.stationId ? RAIL.raised : 'transparent',
        }}
      >
        <span className="h-[7px] w-[7px] flex-shrink-0 rounded-full" style={{ background: '#0f9d63', boxShadow: '0 0 0 3px rgba(15,157,99,.18)' }} />
        <span className="truncate">{orgName || 'Your organisation'}</span>
      </button>

      <p className={label} style={{ color: RAIL.dim }}>
        Stations <span className="ml-1 rounded-full px-1.5 font-mono text-[10px]" style={{ background: 'rgba(255,255,255,.06)' }}>{stations.length}</span>
      </p>

      {stations.map((station) => {
        const lines = productLines(station)
        const isActive = calNav.stationId === station.id
        const tanks = lines.reduce((n, l) => n + l.tanks.length, 0)
        const pumps = lines.reduce((n, l) => n + l.pumps.length, 0)
        const ready = lines.some((l) => l.tanks.length > 0 && l.pumps.length > 0)
        return (
          <div key={station.id}>
            <button
              onClick={() => go({ view: 'station', stationId: station.id })}
              className={`${rowBase} text-[13px] font-medium`}
              style={{
                color: isActive ? RAIL.bright : RAIL.text,
                background: isActive ? 'rgba(226,147,43,.14)' : 'transparent',
              }}
            >
              <span
                className="h-[7px] w-[7px] flex-shrink-0 rounded-full"
                style={ready
                  ? { background: '#0f9d63', boxShadow: '0 0 0 3px rgba(15,157,99,.18)' }
                  : { background: '#e2932b', boxShadow: '0 0 0 3px rgba(226,147,43,.18)' }}
              />
              <span className="flex-1 truncate">{station.name}</span>
              <span className="font-mono text-[10.5px]" style={{ color: isActive ? '#e2932b' : RAIL.dim }}>{tanks}·{pumps}</span>
            </button>

            {isActive && (
              <div className="ml-[19px] border-l pl-3.5" style={{ borderColor: 'rgba(255,255,255,.09)' }}>
                <p className={label} style={{ color: RAIL.dim }}>
                  Products <span className="ml-1 rounded-full px-1.5 font-mono text-[10px]" style={{ background: 'rgba(255,255,255,.06)' }}>{lines.length}</span>
                </p>
                {lines.length === 0 && <p className="px-2 pb-1 text-[12px]" style={{ color: RAIL.dim }}>No products yet</p>}
                {lines.map((line) => (
                  <button
                    key={line.product}
                    onClick={() => go({ view: 'station', stationId: station.id })}
                    className={`${rowBase} text-[12.5px]`}
                    style={{ color: RAIL.text }}
                  >
                    <span className="h-2 w-2 flex-shrink-0 rounded-[2px]" style={{ background: PRODUCTS[line.product]?.color }} />
                    <span className="flex-1 truncate">{line.product} · {line.tanks.length}T·{line.pumps.length}P</span>
                    <span className="font-mono text-[10.5px]" style={{ color: RAIL.dim }}>{sellingMeta(line.tanks)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function SideNav({ activeTab, onTabChange, currentUser, onLogout, organizations = [], stations = [], calNav, onCalNavChange }) {
  return (
    <aside className="fixed inset-y-0 left-0 w-[272px] bg-[#071620] flex flex-col z-50">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
        <div className="w-8 h-8 bg-[#c97a1a] rounded-full flex items-center justify-center flex-shrink-0">
          <Droplet className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <span className="block text-lg font-bold text-[#eef6f4] leading-none">PetroTrack</span>
          {/* Which kind of account this is, stated plainly — the menu differs by
              role, so it should not be something you infer from the menu. */}
          <span className="mt-1 block truncate text-[11px] font-semibold uppercase tracking-[.08em] text-[#e2932b]">
            {ROLE_LABELS[currentUser.role] || currentUser.role}
          </span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {SIDE_NAV_ITEMS.map(({ id, label, Icon }) => {
          const isActive = activeTab === id
          return (
            <div key={id}>
              <button
                onClick={() => onTabChange(id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#c97a1a]/15 text-[#e2932b]'
                    : 'text-[#8fa9b0] hover:bg-white/5 hover:text-[#eef6f4]'
                }`}
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.4 : 1.8} />
                <span className="truncate">{label}</span>
              </button>

              {id === 'calibration' && isActive && calNav && (
                <CalibrationTree
                  organizations={organizations}
                  stations={stations}
                  calNav={calNav}
                  onCalNavChange={onCalNavChange}
                  onTabChange={onTabChange}
                />
              )}
            </div>
          )
        })}
      </nav>

      <div className="border-t border-white/10 p-3">
        <div className="px-2 pb-2">
          <p className="text-sm font-semibold text-[#eef6f4] truncate">{currentUser.name}</p>
          <p className="text-xs text-[#516a72] truncate">{currentUser.email}</p>
        </div>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#8fa9b0] hover:bg-white/5 hover:text-[#eef6f4] transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
          Log out
        </button>
      </div>
    </aside>
  )
}
